import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import useAddressPredictions from 'hooks/useAddressPredictions';
import { checkAddressByGooglePlace } from 'reducers/addressReducer';
import { fetchProperty } from 'reducers/propertyReducer';
import { API_URL } from 'data/services/apiConstants';
import { BOROUGHS } from 'utils/boroughs';
import {
	addressForPrediction,
	mainText,
	secondaryText,
} from 'components/addressSearch/GoogleAddressSearchV2';

export const BOROUGH_NAMES = [
	'Manhattan',
	'Brooklyn',
	'Queens',
	'Bronx',
	'Staten Island',
];

const BOROUGH_IDS = {
	Manhattan: BOROUGHS.MANHATTAN,
	Bronx: BOROUGHS.BRONX,
	Brooklyn: BOROUGHS.BROOKLYN,
	Queens: BOROUGHS.QUEENS,
	'Staten Island': BOROUGHS.STATEN_ISLAND,
};

/**
 * Address search for the marketing header / hero. Same behaviour as the dashboard's
 * GoogleAddressSearchV2 (debounced Google predictions, selection resolved from the
 * prediction, no Place Details call) - only the markup differs.
 */
export function useAddressSearch() {
	const dispatch = useDispatch();
	const [value, setValue] = useState('');
	const [open, setOpen] = useState(false);
	const [highlighted, setHighlighted] = useState(-1);
	const containerRef = useRef(null);

	// Only query while the list is open so committing a selection doesn't trigger one more request.
	const { predictions, clear, startNewSession, endSession } =
		useAddressPredictions(open ? value : '');

	useEffect(() => {
		setHighlighted(-1);
	}, [predictions]);

	useEffect(() => {
		const onPointerDown = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', onPointerDown);
		return () => document.removeEventListener('mousedown', onPointerDown);
	}, []);

	const selectPrediction = useCallback(
		(prediction) => {
			if (!prediction) return;
			const address = addressForPrediction(prediction);
			setValue(address);
			setOpen(false);
			clear();
			endSession();
			dispatch(checkAddressByGooglePlace({ formatted_address: address }, address));
		},
		[clear, endSession, dispatch]
	);

	const onChange = (event) => {
		if (!value) startNewSession();
		setValue(event.target.value);
		setOpen(true);
	};

	const onKeyDown = (event) => {
		if (!open || predictions.length === 0) return;
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setHighlighted((current) => (current + 1) % predictions.length);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setHighlighted((current) =>
				current <= 0 ? predictions.length - 1 : current - 1
			);
		} else if (event.key === 'Enter' && highlighted >= 0) {
			event.preventDefault();
			selectPrediction(predictions[highlighted]);
		} else if (event.key === 'Escape') {
			setOpen(false);
		}
	};

	return {
		value,
		onChange,
		onKeyDown,
		onFocus: () => predictions.length > 0 && setOpen(true),
		containerRef,
		showList: open && predictions.length > 0,
		predictions,
		highlighted,
		setHighlighted,
		selectPrediction,
		mainText,
		secondaryText,
	};
}

/**
 * Block / lot / borough search: resolves the addresses on a tax lot, then opens the picked one.
 */
export function useBlockLotSearch() {
	const dispatch = useDispatch();
	const [block, setBlock] = useState('');
	const [lot, setLot] = useState('');
	const [borough, setBorough] = useState('Manhattan');
	const [addresses, setAddresses] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showList, setShowList] = useState(false);
	const containerRef = useRef(null);

	useEffect(() => {
		if (!showList) return undefined;
		const onPointerDown = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setShowList(false);
			}
		};
		document.addEventListener('mousedown', onPointerDown);
		return () => document.removeEventListener('mousedown', onPointerDown);
	}, [showList]);

	const search = async () => {
		if (!block || !lot) return;
		setLoading(true);
		setShowList(true);
		setAddresses([]);
		try {
			const url = `${API_URL}/api/search/property/addresses-by-block-lot?block=${encodeURIComponent(
				block
			)}&lot=${encodeURIComponent(lot)}&borough=${BOROUGH_IDS[borough]}`;
			const response = await fetch(url);
			const data = await response.json();
			setAddresses(data.addresses || []);
		} catch (error) {
			console.error('Error searching addresses:', error);
			setAddresses([]);
		} finally {
			setLoading(false);
		}
	};

	const selectAddress = (entry) => {
		setShowList(false);
		dispatch(fetchProperty(entry.address));
		dispatch(push(`/property/${encodeURIComponent(entry.address)}`));
	};

	return {
		block,
		setBlock,
		lot,
		setLot,
		borough,
		setBorough,
		addresses,
		loading,
		showList,
		containerRef,
		search,
		selectAddress,
	};
}
